import { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Send, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = () => {
  const [ref, isVisible] = useScrollReveal();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', role: '', message: '', newsletter: false });
  const [loading, setLoading] = useState(false);
  const { t, lang } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.role) {
      toast.error(t.contact.errorRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, { ...formData, language: lang });
      setSubmitted(true);
      toast.success(t.contact.successTitle);
    } catch (err) {
      console.error(err);
      // Fallback to localStorage
      const submissions = JSON.parse(localStorage.getItem('nl_submissions') || '[]');
      submissions.push({ ...formData, timestamp: new Date().toISOString() });
      localStorage.setItem('nl_submissions', JSON.stringify(submissions));
      setSubmitted(true);
      toast.success(t.contact.successTitle);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <section id="kontakt" className="relative py-28 bg-[#0A0A0A]" ref={ref}>
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A84C]/20 to-transparent" />
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center mx-auto mb-6"><CheckCircle className="text-[#C9A84C]" size={28} /></div>
          <h2 className="text-3xl font-bold text-[#F5F0E8] mb-4 font-heading">{t.contact.successTitle}</h2>
          <p className="text-[#9A9590] text-lg">{t.contact.successMessage}</p>
          <Button onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', role: '', message: '', newsletter: false }); }} variant="outline" className="mt-8 border-[#C9A84C]/20 text-[#C9A84C] hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]/40 bg-transparent">{t.contact.successButton}</Button>
        </div>
      </section>
    );
  }

  return (
    <section id="kontakt" className="relative py-28 bg-[#0A0A0A]" ref={ref}>
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A84C]/20 to-transparent" />
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className={`flex items-center justify-center gap-3 mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="h-[1px] w-8 bg-[#C9A84C]" />
            <span className="text-[#C9A84C] text-sm font-medium tracking-[0.2em] uppercase">{t.contact.tag}</span>
            <div className="h-[1px] w-8 bg-[#C9A84C]" />
          </div>
          <h2 className={`text-4xl sm:text-5xl font-bold text-[#F5F0E8] mb-4 font-heading transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t.contact.headline}</h2>
          <p className={`text-[#9A9590] text-lg transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t.contact.subline}</p>
        </div>
        <form onSubmit={handleSubmit} className={`space-y-6 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#9A9590] text-sm">{t.contact.nameLabel}</Label>
              <Input id="name" placeholder={t.contact.namePlaceholder} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-[#111111] border-[#1F1F1F] text-[#F5F0E8] placeholder:text-[#555] focus:border-[#C9A84C]/50 focus:ring-[#C9A84C]/20 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#9A9590] text-sm">{t.contact.emailLabel}</Label>
              <Input id="email" type="email" placeholder={t.contact.emailPlaceholder} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-[#111111] border-[#1F1F1F] text-[#F5F0E8] placeholder:text-[#555] focus:border-[#C9A84C]/50 focus:ring-[#C9A84C]/20 h-12" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#9A9590] text-sm">{t.contact.roleLabel}</Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
              <SelectTrigger className="bg-[#111111] border-[#1F1F1F] text-[#F5F0E8] focus:border-[#C9A84C]/50 focus:ring-[#C9A84C]/20 h-12"><SelectValue placeholder={t.contact.rolePlaceholder} /></SelectTrigger>
              <SelectContent className="bg-[#161616] border-[#1F1F1F]">
                {t.contact.roles.map((role) => (<SelectItem key={role.value} value={role.value} className="text-[#F5F0E8] focus:bg-[#C9A84C]/10 focus:text-[#F5F0E8] cursor-pointer">{role.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="message" className="text-[#9A9590] text-sm">{t.contact.messageLabel}</Label>
            <Textarea id="message" placeholder={t.contact.messagePlaceholder} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="bg-[#111111] border-[#1F1F1F] text-[#F5F0E8] placeholder:text-[#555] focus:border-[#C9A84C]/50 focus:ring-[#C9A84C]/20 min-h-[120px] resize-none" />
          </div>
          <div className="flex items-start gap-3">
            <Checkbox id="newsletter" checked={formData.newsletter} onCheckedChange={(checked) => setFormData({ ...formData, newsletter: checked })} className="border-[#1F1F1F] data-[state=checked]:bg-[#C9A84C] data-[state=checked]:border-[#C9A84C] mt-0.5" />
            <Label htmlFor="newsletter" className="text-[#8A8680] text-sm leading-relaxed cursor-pointer">{t.contact.checkbox}</Label>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#C9A84C] hover:bg-[#D4B85A] text-[#0A0A0A] font-semibold h-13 text-base tracking-wide border-0 shadow-lg shadow-[#C9A84C]/15 gap-2 disabled:opacity-60">{loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{t.contact.submit}</Button>
        </form>
      </div>
    </section>
  );
};
